/// src/api/client.js
const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
if (!API_BASE) {
  // Allow same-origin /api in production if hosted behind a proxy.
  console.warn('[api] REACT_APP_API_URL is not set; falling back to same-origin /api');
}

let activeOrgContext = null;
let authToken =
  typeof window !== 'undefined'
    ? (() => {
        try {
          return localStorage.getItem('token');
        } catch {
          return null;
        }
      })()
    : null;

export function getAuthToken() {
  if (authToken) return authToken;
  if (typeof window === 'undefined') return null;
  try {
    authToken = localStorage.getItem('token');
    return authToken;
  } catch {
    return null;
  }
}

export function setAuthToken(token, { persist = true } = {}) {
  authToken = token || null;
  if (typeof window === 'undefined') return;
  try {
    if (persist) {
      if (token) localStorage.setItem('token', token);
      else localStorage.removeItem('token');
    } else if (!persist && token) {
      localStorage.removeItem('token');
    }
    if (!token && !persist) {
      // ensure storage cleared
      localStorage.removeItem('token');
    }
  } catch {
    // ignore storage issues
  }
}

export function clearAuthToken({ persist = true } = {}) {
  setAuthToken(null, { persist });
}

function coerceOrgContext(ctx) {
  if (!ctx || !ctx.orgId) return null;
  return {
    orgId: String(ctx.orgId),
    orgType: (ctx.orgType || 'account').toLowerCase(),
  };
}

function readStoredOrgContext() {
  if (typeof window === 'undefined') return null;
  try {
    const rawCtx = localStorage.getItem('activeOrgContext');
    if (rawCtx) {
      const parsed = JSON.parse(rawCtx);
      if (parsed?.orgId) {
        return {
          orgId: String(parsed.orgId),
          orgType: String(parsed.orgType || 'account').toLowerCase(),
        };
      }
    }
    const stored = localStorage.getItem('activeAccountId');
    if (!stored) return null;
    return { orgId: stored, orgType: 'account' };
  } catch {
    return null;
  }
}

function resolveOrgContext(overrideCtx) {
  const explicit = coerceOrgContext(overrideCtx);
  if (explicit) {
    activeOrgContext = explicit;
    return activeOrgContext;
  }
  if (activeOrgContext && activeOrgContext.orgId) {
    return activeOrgContext;
  }
  const stored = readStoredOrgContext();
  if (stored) {
    activeOrgContext = stored;
    return stored;
  }
  return null;
}

export function setOrgContext(ctx) {
  const normalized = coerceOrgContext(ctx);
  activeOrgContext = normalized;
  if (typeof window === 'undefined') return;
  try {
    if (normalized?.orgId) {
      localStorage.setItem('activeAccountId', normalized.orgId);
      localStorage.setItem('activeOrgContext', JSON.stringify(normalized));
    } else {
      localStorage.removeItem('activeAccountId');
      localStorage.removeItem('activeOrgContext');
    }
  } catch {
    // ignore storage errors (e.g., private mode)
  }
}

export function getOrgContext() {
  return activeOrgContext || readStoredOrgContext();
}

export async function apiFetch(path, opts = {}) {
  const token = getAuthToken();
  const { orgContext: overrideOrgContext, headers: headerOverrides, ...restOpts } = opts;

  // Build URL: absolute stays as-is; relative gets API_BASE + /api + path
  const url = /^https?:\/\//i.test(path)
    ? path
    : `${API_BASE}${path.startsWith('/api') ? '' : '/api'}${path.startsWith('/') ? '' : '/'}${path}`;

  const headers = {
    'Content-Type': 'application/json',
    ...(headerOverrides || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const context = resolveOrgContext(overrideOrgContext);
  if (context?.orgId) {
    headers['X-Org-Id'] = context.orgId;
    headers['X-Org-Type'] = context.orgType || 'account';
  }

  let body = restOpts.body;
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
  if (body != null && !isFormData && typeof body !== 'string') {
    body = JSON.stringify(body);
  }
  if (isFormData) {
    delete headers['Content-Type'];
  }

  const res = await fetch(url, {
    method: restOpts.method || 'GET',
    credentials: 'include',          // for cookie sessions
    ...restOpts,
    body,
    headers,
  });

  const contentType = res.headers.get('content-type') || '';
  const isJson = /\bapplication\/json\b/i.test(contentType);

  if (!res.ok) {
    let data = null;
    if (isJson) {
      try { data = await res.json(); } catch {}
    } else {
      try {
        const raw = await res.text();
        data = { error: raw?.slice(0, 256) || 'Request failed' };
      } catch {
        data = null;
      }
    }
    const err = new Error(data?.error || data?.message || 'Request failed');
    err.status = res.status;
    err.data = data;
    throw err;
  }

  // Handle 204 No Content
  if (res.status === 204) return null;

  if (!isJson) {
    const raw = await res.text().catch(() => '');
    const err = new Error('Unexpected response format (expected JSON)');
    err.status = res.status;
    err.data = { raw };
    throw err;
  }

  return res.json();
}

export async function apiDownload(path, opts = {}) {
  const token = getAuthToken();
  const { orgContext: overrideOrgContext, headers: headerOverrides, filename: fallbackFilename = 'download.csv', ...restOpts } = opts;
  const url = /^https?:\/\//i.test(path)
    ? path
    : `${API_BASE}${path.startsWith('/api') ? '' : '/api'}${path.startsWith('/') ? '' : '/'}${path}`;

  const headers = {
    ...(headerOverrides || {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const context = resolveOrgContext(overrideOrgContext);
  if (context?.orgId) {
    headers['X-Org-Id'] = context.orgId;
    headers['X-Org-Type'] = context.orgType || 'account';
  }

  const res = await fetch(url, {
    method: restOpts.method || 'GET',
    credentials: 'include',
    ...restOpts,
    headers,
  });

  if (!res.ok) {
    const contentType = res.headers.get('content-type') || '';
    let data = null;
    if (/\bapplication\/json\b/i.test(contentType)) {
      try { data = await res.json(); } catch {}
    } else {
      try {
        const raw = await res.text();
        data = { error: raw?.slice(0, 256) || 'Request failed' };
      } catch {
        data = null;
      }
    }
    const err = new Error(data?.error || data?.message || 'Request failed');
    err.status = res.status;
    err.data = data;
    throw err;
  }

  const blob = await res.blob();
  const disposition = res.headers.get('content-disposition') || '';
  const match = disposition.match(/filename="?([^";]+)"?/i);
  const filename = match?.[1] || fallbackFilename;
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
  return { filename };
}
